import CheckCircleOutlineOutlined from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import SyncDisabledOutlined from "@mui/icons-material/SyncDisabledOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../../../components/InHouseTooltip/InHouseTooltip";

export interface SyncStatusIconProps {
	syncedCount: number;
	totalCount: number;
	testId: string;
	/** Role string is set to Don't Sync — show the neutral "Not syncing" state instead of a count. */
	dontSync?: boolean;
}

/** At-a-glance icon: not syncing (Don't Sync), all employees syncing, mixed, or none. */
export const SyncStatusIcon: FC<SyncStatusIconProps> = ({ syncedCount, totalCount, testId, dontSync = false }) => {
	const { t } = useTranslation();
	if (totalCount === 0) return null;
	if (dontSync) {
		return (
			<InHouseTooltip title={t("hris.mapping.rawEmployees.syncStatus.dontSync", { count: totalCount })}>
				<SyncDisabledOutlined color="disabled" fontSize="small" data-testid={testId} data-sync-state="dontSync" />
			</InHouseTooltip>
		);
	}
	const state: "all" | "partial" | "none" = syncedCount === totalCount ? "all" : syncedCount === 0 ? "none" : "partial";
	const tooltip = t(`hris.mapping.rawEmployees.syncStatus.${state === "all" ? "allSynced" : state === "none" ? "noneSynced" : "partial"}`, {
		count: totalCount,
		synced: syncedCount,
		total: totalCount,
	});
	const common = { fontSize: "small" as const, "data-testid": testId, "data-sync-state": state };
	return (
		<InHouseTooltip title={tooltip}>
			{state === "all" ? (
				<CheckCircleOutlineOutlined color="success" {...common} />
			) : state === "none" ? (
				<ErrorOutlineOutlined color="error" {...common} />
			) : (
				<WarningAmberOutlined color="warning" {...common} />
			)}
		</InHouseTooltip>
	);
};
